import { useId, useState } from 'react';
import { analyzeManualMeasurements, validateManualMeasurements } from '../lib/faceGeometry';
import type { FaceAnalysis, JawCharacter, ManualMeasurements } from '../types';
import { AlertIcon, LockIcon, RulerIcon, SparkIcon } from './Icons';
import { ShapeGlyph } from './ShapeGlyph';

type MeasurementUnit = 'cm' | 'in';
type MeasurementField = 'faceLength' | 'foreheadWidth' | 'cheekboneWidth' | 'jawWidth';

interface ManualAnalyzerProps {
  onAnalysisChange: (analysis: FaceAnalysis) => void;
}

interface MeasurementDefinition {
  key: MeasurementField;
  label: string;
  shortLabel: string;
  instruction: string;
  placeholderCm: string;
  placeholderIn: string;
}

const MEASUREMENTS: MeasurementDefinition[] = [
  {
    key: 'faceLength',
    label: 'Face length',
    shortLabel: 'Length',
    instruction: 'Hairline center to the lowest point of the chin.',
    placeholderCm: '19.5',
    placeholderIn: '7.7',
  },
  {
    key: 'foreheadWidth',
    label: 'Forehead width',
    shortLabel: 'Forehead',
    instruction: 'Straight across the widest visible forehead area.',
    placeholderCm: '13.0',
    placeholderIn: '5.1',
  },
  {
    key: 'cheekboneWidth',
    label: 'Cheekbone width',
    shortLabel: 'Cheekbones',
    instruction: 'Across the widest points of the upper cheeks.',
    placeholderCm: '14.0',
    placeholderIn: '5.5',
  },
  {
    key: 'jawWidth',
    label: 'Jaw width',
    shortLabel: 'Jaw',
    instruction: 'Straight across the jaw at its widest visible point.',
    placeholderCm: '11.8',
    placeholderIn: '4.6',
  },
];

const JAW_OPTIONS: Array<{ id: JawCharacter; label: string; description: string }> = [
  { id: 'soft', label: 'Soft curve', description: 'No distinct corner; the line rounds gradually.' },
  { id: 'angular', label: 'Angular', description: 'A visible corner where the jaw turns toward the chin.' },
  { id: 'tapered', label: 'Tapered', description: 'The lower face narrows noticeably toward the chin.' },
];

const EMPTY_VALUES: Record<MeasurementField, string> = {
  faceLength: '',
  foreheadWidth: '',
  cheekboneWidth: '',
  jawWidth: '',
};

function parseValues(
  values: Record<MeasurementField, string>,
  jawCharacter: JawCharacter,
): Partial<ManualMeasurements> {
  const parsed: Partial<ManualMeasurements> = { jawCharacter };
  for (const definition of MEASUREMENTS) {
    parsed[definition.key] = Number.parseFloat(values[definition.key]);
  }
  return parsed;
}

const EXAMPLE_VALUES: Record<MeasurementUnit, Record<MeasurementField, string>> = {
  cm: { faceLength: '19.5', foreheadWidth: '13.0', cheekboneWidth: '14.0', jawWidth: '11.8' },
  in: { faceLength: '7.7', foreheadWidth: '5.1', cheekboneWidth: '5.5', jawWidth: '4.6' },
};

export function ManualAnalyzer({ onAnalysisChange }: ManualAnalyzerProps): React.ReactElement {
  const formId = useId();
  const [unit, setUnit] = useState<MeasurementUnit>('cm');
  const [values, setValues] = useState<Record<MeasurementField, string>>(EMPTY_VALUES);
  const [jawCharacter, setJawCharacter] = useState<JawCharacter>('soft');
  const [errors, setErrors] = useState<string[]>([]);
  const [exampleLoaded, setExampleLoaded] = useState(false);

  const loadExample = () => {
    setValues({ ...EXAMPLE_VALUES[unit] });
    setJawCharacter('tapered');
    setErrors([]);
    setExampleLoaded(true);
  };

  const switchUnit = (nextUnit: MeasurementUnit) => {
    if (unit === nextUnit) return;
    const factor = nextUnit === 'in' ? 1 / 2.54 : 2.54;
    setValues((current) => {
      const converted = { ...current };
      for (const definition of MEASUREMENTS) {
        const value = Number.parseFloat(current[definition.key]);
        converted[definition.key] = Number.isFinite(value)
          ? (value * factor).toFixed(nextUnit === 'in' ? 2 : 1)
          : '';
      }
      return converted;
    });
    setUnit(nextUnit);
  };

  const submit = () => {
    const parsed = parseValues(values, jawCharacter);
    const nextErrors = validateManualMeasurements(parsed);
    if (nextErrors.length > 0) {
      setErrors(nextErrors);
      return;
    }

    setErrors([]);
    onAnalysisChange(analyzeManualMeasurements(parsed as ManualMeasurements));
  };

  return (
    <section id="manual-panel" className="analyzer-panel manual-analyzer" role="tabpanel" aria-labelledby="manual-tab">
      <div className="manual-analyzer__guide">
        <div className="manual-analyzer__art" aria-hidden="true">
          <ShapeGlyph shape="oval" measured />
          <span className="guide-label guide-label--length">Length</span>
          <span className="guide-label guide-label--forehead">Forehead</span>
          <span className="guide-label guide-label--cheeks">Cheekbones</span>
          <span className="guide-label guide-label--jaw">Jaw</span>
        </div>
        <div>
          <span className="eyebrow">Four straight measurements</span>
          <h2>Use a mirror and a soft tape</h2>
          <p>
            Pull hair away from the face. Keep the tape straight rather than following the curve of your skin. Ratios matter more than the exact unit.
          </p>
        </div>
      </div>

      <form
        className="measurement-form"
        aria-describedby={`${formId}-measurement-help`}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className="measurement-form__heading">
          <div>
            <span className="eyebrow">Enter your measurements</span>
            <p id={`${formId}-measurement-help`}>Use the same unit for every field.</p>
            <button type="button" className="text-button" onClick={loadExample}>
              Load example values
            </button>
          </div>
          <div className="unit-switch" aria-label="Measurement unit">
            <button type="button" aria-pressed={unit === 'cm'} onClick={() => switchUnit('cm')}>
              cm
            </button>
            <button type="button" aria-pressed={unit === 'in'} onClick={() => switchUnit('in')}>
              inches
            </button>
          </div>
        </div>

        <div className="measurement-grid">
          {MEASUREMENTS.map((definition, index) => {
            const inputId = `${formId}-${definition.key}`;
            const helpId = `${inputId}-help`;
            return (
              <label className="measurement-field" htmlFor={inputId} key={definition.key}>
                <span className="measurement-field__number" aria-hidden="true">
                  0{index + 1}
                </span>
                <span className="measurement-field__copy">
                  <strong>{definition.label}</strong>
                  <small id={helpId}>{definition.instruction}</small>
                </span>
                <span className="measurement-field__input">
                  <input
                    id={inputId}
                    name={definition.key}
                    type="number"
                    inputMode="decimal"
                    min="0.1"
                    step="0.01"
                    value={values[definition.key]}
                    placeholder={unit === 'cm' ? definition.placeholderCm : definition.placeholderIn}
                    aria-describedby={helpId}
                    onChange={(event) => {
                      const value = event.currentTarget.value;
                      setExampleLoaded(false);
                      setValues((current) => ({ ...current, [definition.key]: value }));
                    }}
                  />
                  <span aria-hidden="true">{unit}</span>
                </span>
              </label>
            );
          })}
        </div>

        <fieldset className="jaw-fieldset">
          <legend>How does the jaw turn toward the chin?</legend>
          <div className="jaw-options">
            {JAW_OPTIONS.map((option) => (
              <label className="jaw-option" key={option.id}>
                <input
                  type="radio"
                  name="jawCharacter"
                  value={option.id}
                  checked={jawCharacter === option.id}
                  onChange={() => setJawCharacter(option.id)}
                />
                <span className={`jaw-option__glyph jaw-option__glyph--${option.id}`} aria-hidden="true" />
                <span>
                  <strong>{option.label}</strong>
                  <small>{option.description}</small>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <p className="measurement-form__note" role="status" aria-live="polite">
          {exampleLoaded
            ? 'Example loaded: an adult face that measures close to the oval profile. Replace the numbers with your own, then calculate.'
            : ''}
        </p>

        {errors.length > 0 ? (
          <div className="callout callout--error" role="alert">
            <AlertIcon />
            <div>
              <strong>Check the measurements.</strong>
              <ul>
                {errors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}

        <button className="button button--primary button--wide" type="submit">
          <RulerIcon />
          Calculate my face shape
        </button>
      </form>

      <div className="privacy-inline">
        <LockIcon />
        <p>
          <strong>No measurements are stored or sent.</strong> The ratio calculation runs in this page and resets when you refresh it.
        </p>
        <SparkIcon aria-hidden="true" />
      </div>
    </section>
  );
}
